<template>
  <div>
    <StockDocumentPageHeading
      :title="$t('STOCK.REVISIONS.PAGE_TITLE')"
      :code="revision ? revision.code : ''"
    />

    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>

      <v-toolbar flat dense>
        <v-chip v-if="revision" small dark :color="statusColor(revision.status)" class="mr-3">
          {{ statusLabel(revision.status) }}
        </v-chip>
        <v-toolbar-title v-if="revision" class="subtitle-1">
          {{ warehouseLabel(revision) }} · {{ revision.for_date }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="canReopen"
          small
          text
          color="teal darken-1"
          :loading="isProcessing"
          @click="showReopenConfirm = true"
        >
          {{ $t("STOCK.REVISIONS.REOPEN") }}
        </v-btn>
        <v-btn
          v-if="isDraft"
          small
          text
          color="primary"
          @click="$router.push({ name: 'stock-revisions-edit', params: { id: revisionId } })"
        >
          {{ $t("GENERAL.LABELS.EDIT") }}
        </v-btn>
      </v-toolbar>

      <v-card-text v-if="revision">
        <v-row dense>
          <v-col cols="12" md="3">
            <div class="caption grey--text text--darken-1">
              {{ $t("STOCK.REVISIONS.COMPANY") }}
            </div>
            <div>{{ organizationLabel(revision) }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="caption grey--text text--darken-1">
              {{ $t("STOCK.REVISIONS.PREVIOUS_REVISION") }}
            </div>
            <div>
              <router-link
                v-if="revision.previous_revision"
                :to="{ name: 'stock-revisions-view', params: { id: revision.previous_revision.id } }"
              >
                {{ revision.previous_revision.code }} ({{ revision.previous_revision.for_date }})
              </router-link>
              <span v-else>{{ $t("STOCK.REVISIONS.NO_FINALIZED_REVISION") }}</span>
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="caption grey--text text--darken-1">
              {{ $t("STOCK.REVISIONS.FINALIZED_MOVEMENT") }}
            </div>
            <div>
              <router-link
                v-if="revision.stock_action"
                :to="{ name: 'stock-receipts-view', params: { id: revision.stock_action.id } }"
              >
                {{ revision.stock_action.code }}
              </router-link>
              <span v-else>-</span>
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="caption grey--text text--darken-1">
              {{ $t("STOCK.REVISIONS.FINALIZED_BY") }}
            </div>
            <div>
              {{ (revision.finalized_by_user && revision.finalized_by_user.name) || "-" }}
            </div>
          </v-col>
          <v-col v-if="revision.notes" cols="12">
            <div class="caption grey--text text--darken-1">
              {{ $t("STOCK.REVISIONS.NOTES") }}
            </div>
            <div>{{ revision.notes }}</div>
          </v-col>
        </v-row>

        <div v-if="summary" class="mt-3">
          <div class="caption grey--text text--darken-1 mb-1">
            {{ $t("STOCK.REVISIONS.SUMMARY.TITLE") }}
          </div>
          <div class="d-flex flex-wrap">
            <v-chip small class="mr-2 mb-2" color="grey lighten-3">
              <span class="font-weight-medium mr-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.TOTAL_ROWS") }}:
              </span>
              {{ summary.total_rows }}
            </v-chip>
            <v-chip small class="mr-2 mb-2" color="grey lighten-3">
              <span class="font-weight-medium mr-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.COUNTED_ROWS") }}:
              </span>
              {{ summary.counted_rows }}
            </v-chip>
            <v-chip small class="mr-2 mb-2" color="grey lighten-3">
              <span class="font-weight-medium mr-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.VARIANCE_ROWS") }}:
              </span>
              {{ summary.variance_rows }}
            </v-chip>
            <v-chip small class="mr-2 mb-2" color="grey lighten-3">
              <span class="font-weight-medium mr-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.VARIANCE_QUANTITY") }}:
              </span>
              {{ formatSignedQuantity(summary.variance_quantity) }}
            </v-chip>
            <v-chip small class="mr-2 mb-2" color="grey lighten-3">
              <span class="font-weight-medium mr-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.VARIANCE_VALUE") }}:
              </span>
              {{ formatSignedMoney(summary.variance_value, summary.currency_code) }}
            </v-chip>
            <v-chip small class="mr-2 mb-2" color="grey lighten-3">
              <span class="font-weight-medium mr-1">
                {{ $t("STOCK.REVISIONS.SUMMARY.CONSUMPTION_VALUE") }}:
              </span>
              {{ formatMoney(summary.consumption_value, summary.currency_code) }}
            </v-chip>
          </div>
        </div>
      </v-card-text>

      <StockRevisionCountSheet
        v-if="revision"
        ref="sheet"
        :revision-id="revisionId"
        :with-lots="isFinalized"
        @open-lots="openLots"
        @error="showError"
      />

      <v-card-text v-if="statusTimeline.length">
        <v-subheader class="px-0">
          {{ $t("STOCK.REVISIONS.STATUS_TIMELINE") }}
        </v-subheader>
        <v-timeline dense>
          <v-timeline-item
            v-for="entry in statusTimeline"
            :key="entry.id"
            small
            :color="timelineEntryColor(entry)"
          >
            <div class="subtitle-2">{{ timelineEntryLabel(entry) }}</div>
            <div class="caption grey--text">
              {{ formatTimelineDate(entry.changed_at) }}
              <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                · {{ entry.changed_by_user.name }}
              </span>
            </div>
            <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>

    <StockRevisionLotsDialog
      v-model="showLotsDialog"
      :revision-id="revisionId"
      :item-id="lotsItemId"
      @saved="handleLotsSaved"
    />

    <TPConfirm
      v-model="showReopenConfirm"
      :content="reopenConfirmMessage"
      :confirm-label="$t('STOCK.REVISIONS.REOPEN')"
      @confirm="reopenRevision"
    ></TPConfirm>
  </div>
</template>

<script>
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockRevisionCountSheet from "@/views/partials/stock/revisions/StockRevisionCountSheet.vue";
import StockRevisionLotsDialog from "@/views/partials/stock/revisions/StockRevisionLotsDialog.vue";
import stockRevisionDisplayMixin from "@/views/partials/stock/revisions/stockRevisionDisplay.mixin";
import stockStatusTimelineMixin from "@/views/partials/stock/stockStatusTimeline.mixin";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_STOCK_REVISION,
  GET_STOCK_REVISION_SUMMARY,
  REOPEN_STOCK_REVISION,
} from "@/store/stock/stockRevisions.module";

export default {
  name: "StockViewRevision",
  components: {
    TPConfirm,
    StockDocumentPageHeading,
    StockRevisionCountSheet,
    StockRevisionLotsDialog,
  },
  mixins: [stockRevisionDisplayMixin, stockStatusTimelineMixin],
  data() {
    return {
      revision: null,
      summary: null,
      isProcessing: false,
      showLotsDialog: false,
      showReopenConfirm: false,
      lotsItemId: null,
      statusTimelineI18nPrefix: "STOCK.REVISIONS.STATUSES",
      statusTimelineColorMap: {
        draft: "grey",
        finalized: "success",
        cancelled: "error",
      },
    };
  },
  computed: {
    /** The timeline mixin reads the document from this key. */
    document() {
      return this.revision;
    },
    revisionId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    isDraft() {
      return Boolean(this.revision && this.revision.status === "draft");
    },
    isFinalized() {
      return Boolean(this.revision && this.revision.status === "finalized");
    },
    canReopen() {
      return Boolean(this.revision && this.revision.can_reopen);
    },
    reopenConfirmMessage() {
      if (!this.revision) {
        return "";
      }
      return this.$t("STOCK.REVISIONS.REOPEN_CONFIRM", { code: this.revision.code });
    },
  },
  mounted() {
    this.load();
  },
  watch: {
    revisionId(id, previousId) {
      if (!id || id === previousId) {
        return;
      }
      if (this.revision && String(this.revision.id) === String(id)) {
        return;
      }
      this.load();
    },
  },
  methods: {
    async load() {
      this.isProcessing = true;
      try {
        const [revision, summary] = await Promise.all([
          this.$store.dispatch(GET_STOCK_REVISION, this.revisionId),
          this.$store.dispatch(GET_STOCK_REVISION_SUMMARY, { id: this.revisionId }),
        ]);
        this.revision = revision;
        this.summary = summary;
      } catch (error) {
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    openLots(row) {
      this.lotsItemId = row.stock_revision_item_id;
      this.showLotsDialog = true;
    },
    handleLotsSaved({ meta }) {
      const failures = (meta && meta.reallocation_failures) || [];
      if (failures.length) {
        this.$store.dispatch(SHOW_SNACK, {
          message: `${this.$t("STOCK.REVISIONS.REALLOCATION_FAILURES.TITLE")}: ${failures.join(" | ")}`,
          type: "warning",
        });
      }
      this.$refs.sheet?.refresh();
    },
    async reopenRevision() {
      this.isProcessing = true;
      try {
        const { revision, meta } = await this.$store.dispatch(
          REOPEN_STOCK_REVISION,
          this.revisionId
        );
        const failures = (meta && meta.reallocation_failures) || [];
        if (failures.length) {
          this.$store.dispatch(SHOW_SNACK, {
            message: `${this.$t("STOCK.REVISIONS.REALLOCATION_FAILURES.TITLE")}: ${failures.join(" | ")}`,
            type: "warning",
          });
        }
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.REVISIONS.REOPENED"),
          type: "success",
        });
        this.$router.push({ name: "stock-revisions-edit", params: { id: revision.id } });
      } catch (error) {
        this.showError(error);
      } finally {
        this.isProcessing = false;
      }
    },
    showError(error) {
      this.$store.dispatch(SHOW_SNACK, {
        message: (error && error.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
        type: "error",
      });
    },
  },
};
</script>
